import { ChevronDownIcon } from "@chakra-ui/icons";
import { AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, Button, Flex, Icon, IconButton, Input, Menu, MenuButton, MenuItem, MenuList, Portal, Td, Tr, useDisclosure } from "@chakra-ui/react";
import { Tooltip } from "../../../../common/components/Tooltip";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaCheck, FaPen, FaTimes, FaTrash } from "react-icons/fa";
import {
  ContactPersonDto,
  useEditContactPersonMutation,
  useRemoveContactPersonMutation,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { displayPhoneNumber } from "../../../../common/formatting/displayPhoneNumber";
import { useSub } from "../../../../common/subscription/useSub";
import { useBaseData } from "../../../../common/useBaseData";
import { ContactKindTag } from "./ContactKindTag";
import { ContactSubject } from "./contactSubject";
import { useCallingCodesByName } from "../../../../common/countries/countryNames";

export const ContactPersonRow = ({ person, subject }: { person: ContactPersonDto; subject: ContactSubject }) => {
  const { t } = useTranslation();
  const baseData = useBaseData();
  const [editContactPerson, { isLoading: isEditing }] = useEditContactPersonMutation();
  const [removeContactPerson, { isLoading: isRemoving }] = useRemoveContactPersonMutation();
  const displayer = useApiError();

  const [editablePerson, setEditablePerson] = useState<ContactPersonDto>(person);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const { canUpdateContactPersons: canUpdate } = useSub();

  const handleDelete = async () => {
    await removeContactPerson({
      subjectType: subject.subjectType,
      subjectId: subject.subjectId,
      email: editablePerson.email,
    })
      .unwrap()
      .catch((e) => {
        displayer.trigger(e);
        throw e;
      });
    onDeleteClose();
  };

  const countryAlpha3Codes = baseData.countries.map((country) => country.alpha3Code);
  const callingCodes = baseData.callingCodes.filter(
    (callingCode) => countryAlpha3Codes.indexOf(callingCode.countryAlpha3Code) !== -1
  );
  const callingCodeOptions = useCallingCodesByName(callingCodes);
  const selectedCallingCodeAlpha3 = editablePerson.phoneNumber?.callingCode.countryAlpha3Code ?? "";

  const handleCallingCodeChange = (alpha3Code: string) => {
    const matchedCallingCode = callingCodes.find((code) => code.countryAlpha3Code === alpha3Code);
    setEditablePerson((current) => ({
      ...current,
      phoneNumber: {
        number: current.phoneNumber?.number ?? "",
        callingCode: {
          countryAlpha3Code: alpha3Code,
          value: matchedCallingCode?.value ?? "",
          countryName: matchedCallingCode?.countryName ?? "",
        },
      },
    }));
  };

  const handleNumberChange = (nextNumber: string) =>
    setEditablePerson((current) => {
      const callingCode = current.phoneNumber?.callingCode ?? {
        countryAlpha3Code: "",
        value: "",
        countryName: "",
      };

      return {
        ...current,
        phoneNumber: { number: nextNumber, callingCode },
      };
    });

  if (isFormVisible)
    return (
      <Tr key={editablePerson.email}>
        <Td>
          <Input
            size="sm"
            rounded="md"
            backgroundColor="smBackground"
            value={editablePerson.name}
            onChange={(e) => setEditablePerson({ ...editablePerson, name: e.target.value })}
          />
        </Td>
        <Td>{editablePerson.email}</Td>
        <Td>
          <Flex alignItems="center" flexWrap="nowrap">
            <Menu>
              <MenuButton
                as={Button}
                size="sm"
                rightIcon={<ChevronDownIcon />}
                rounded="md"
                roundedRight="none"
                borderRightRadius="none"
                flexShrink={0}
                minW="20"
              >
                {selectedCallingCodeAlpha3
                  ? `+${callingCodes.find((code) => code.countryAlpha3Code === selectedCallingCodeAlpha3)?.value ?? ""}`
                  : t("Calling code")}
              </MenuButton>
              <Portal>
                <MenuList width="60" maxHeight="96" overflowY="auto">
                  {callingCodeOptions.map((callingCode) => (
                    <MenuItem
                      key={callingCode.countryAlpha3Code + callingCode.value}
                      onClick={() => handleCallingCodeChange(callingCode.countryAlpha3Code)}
                    >
                      {callingCode.countryName} (+{callingCode.value})
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
            <Input
              flex="0 0 24"
              width="24"
              size="sm"
              rounded="md"
              roundedLeft="none"
              borderLeft="none"
              backgroundColor="smBackground"
              placeholder={t("Phone number") ?? ""}
              type="tel"
              value={editablePerson.phoneNumber?.number ?? ""}
              onChange={(event) => handleNumberChange(event.target.value)}
            />
          </Flex>
        </Td>
        <Td minWidth="40">
          <Input
            size="sm"
            rounded="md"
            backgroundColor="smBackground"
            value={editablePerson.title ?? ""}
            onChange={(e) => setEditablePerson({ ...editablePerson, title: e.target.value })}
          />
        </Td>
        <Td textAlign="center">{subject.name}</Td>
        <Td textAlign="center">
          <ContactKindTag kind="addedByYou" size="sm" />
        </Td>
        <Td>
          <Flex gap={2}>
            <IconButton
              aria-label={t("Cancel")}
              variant="ghost"
              icon={<Icon as={FaTimes} />}
              onClick={() => {
                setEditablePerson(person);
                setIsFormVisible(false);
              }}
            />
            <IconButton
              aria-label={t("Save")}
              icon={<Icon as={FaCheck} />}
              isLoading={isEditing}
              onClick={async () => {
                const contact = {
                  ...editablePerson,
                  phoneNumber: editablePerson.phoneNumber?.number ? editablePerson.phoneNumber : undefined,
                };
                await editContactPerson({
                  subjectType: subject.subjectType,
                  subjectId: subject.subjectId,
                  editContactPersonRequest: { contact },
                })
                  .unwrap()
                  .catch((e) => {
                    displayer.trigger(e);
                    throw e;
                  });
                setIsFormVisible(false);
              }}
            />
          </Flex>
        </Td>
      </Tr>
    );

  return (
    <>
      <Tr key={editablePerson.email}>
        <Td>{editablePerson.name}</Td>
        <Td>{editablePerson.email}</Td>
        <Td whiteSpace="nowrap">{editablePerson.phoneNumber ? displayPhoneNumber(editablePerson.phoneNumber) : null}</Td>
        <Td>{editablePerson.title}</Td>
        <Td textAlign="center">{subject.name}</Td>
        <Td textAlign="center">
          <ContactKindTag kind="addedByYou" size="sm" />
        </Td>
        <Td>
          <Flex gap={2}>
            <Tooltip label={t("Edit contact person")}>
              <IconButton
                aria-label={t("Edit contact person")}
                size="sm"
                variant="ghost"
                colorScheme="teal"
                isDisabled={!canUpdate}
                icon={<Icon as={FaPen} />}
                onClick={() => setIsFormVisible(true)}
              />
            </Tooltip>
            <Tooltip label={t("Delete contact person")}>
              <IconButton
                aria-label={t("Delete contact person")}
                size="sm"
                variant="ghost"
                colorScheme="red"
                isDisabled={!canUpdate}
                icon={<Icon as={FaTrash} />}
                onClick={onDeleteOpen}
              />
            </Tooltip>
          </Flex>
        </Td>
      </Tr>
      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t("Delete Contact Person")}
            </AlertDialogHeader>
            <AlertDialogBody>
              {`${t("Are you sure you want to delete this contact person")}? ${t("This action cannot be undone")}.`}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                {t("Cancel")}
              </Button>
              <Button colorScheme="red" ml={3} isLoading={isRemoving} onClick={handleDelete}>
                {t("Delete")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
